/**
 * API Monitor for tracking and analyzing API requests
 */
class ApiMonitor {
  constructor() {
    this.requests = new Map();
    this.stats = {
      totalRequests: 0,
      totalErrors: 0,
      slowRequests: 0,
      averageResponseTime: 0
    };
    this.isDevelopment = import.meta.env.MODE === 'development';
    this.slowRequestThreshold = 1000; // 1 second
    this.requestLimitPerMinute = 60; // Warn if endpoint called more than 60 times per minute
  }

  /**
   * Start monitoring a request
   */
  startRequest(endpoint, method = 'GET') {
    const requestId = `${Date.now()}_${Math.random()}`;
    const request = {
      id: requestId,
      endpoint,
      method,
      startTime: Date.now(),
      endTime: null,
      duration: null,
      success: null,
      error: null
    };

    this.requests.set(requestId, request);
    this.stats.totalRequests++;

    // Track endpoint frequency
    this.trackEndpointFrequency(endpoint);

    if (this.isDevelopment) {
      console.log(`📡 [API Monitor] Started ${method} ${endpoint} (ID: ${requestId})`);
    }

    return requestId;
  }

  /**
   * End monitoring a request
   */
  endRequest(requestId, success = true, error = null) {
    const request = this.requests.get(requestId);
    if (!request) return;

    const endTime = Date.now();
    const duration = endTime - request.startTime;

    request.endTime = endTime;
    request.duration = duration;
    request.success = success;
    request.error = error;

    // Update statistics
    if (!success) {
      this.stats.totalErrors++;
    }

    if (duration > this.slowRequestThreshold) {
      this.stats.slowRequests++;
      if (this.isDevelopment) {
        console.warn(`🐌 [API Monitor] Slow request detected: ${request.method} ${request.endpoint} took ${duration}ms`);
      }
    }

    // Update average response time
    this.updateAverageResponseTime(duration);

    if (this.isDevelopment) {
      const status = success ? '✅' : '❌';
      console.log(`${status} [API Monitor] Completed ${request.method} ${request.endpoint} in ${duration}ms`);
      
      if (error) {
        console.error(`❌ [API Monitor] Error:`, error);
      }
    }

    // Clean up old requests (keep only last 100)
    if (this.requests.size > 100) {
      const oldestKey = this.requests.keys().next().value;
      this.requests.delete(oldestKey);
    }
  }

  /**
   * Track how often each endpoint is called
   */
  trackEndpointFrequency(endpoint) {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Initialize endpoint tracking if not exists
    if (!this.endpointFrequency) {
      this.endpointFrequency = new Map();
    }

    if (!this.endpointFrequency.has(endpoint)) {
      this.endpointFrequency.set(endpoint, []);
    }

    const timestamps = this.endpointFrequency.get(endpoint);
    
    // Add current timestamp
    timestamps.push(now);
    
    // Remove timestamps older than 1 minute
    const recentTimestamps = timestamps.filter(timestamp => timestamp > oneMinuteAgo);
    this.endpointFrequency.set(endpoint, recentTimestamps);

    // Warn if endpoint is called too frequently
    if (recentTimestamps.length > this.requestLimitPerMinute && this.isDevelopment) {
      console.warn(`⚠️ [API Monitor] High frequency detected: ${endpoint} called ${recentTimestamps.length} times in the last minute`);
    }
  }

  /**
   * Update average response time
   */
  updateAverageResponseTime(duration) {
    const totalDuration = this.stats.averageResponseTime * (this.stats.totalRequests - 1) + duration;
    this.stats.averageResponseTime = Math.round(totalDuration / this.stats.totalRequests);
  }

  /**
   * Get current statistics
   */
  getStats() {
    const recentRequests = Array.from(this.requests.values())
      .filter(req => req.endTime && (Date.now() - req.endTime) < 300000) // Last 5 minutes
      .sort((a, b) => b.endTime - a.endTime);

    const endpointStats = Array.from(this.endpointFrequency?.entries() || [])
      .map(([endpoint, timestamps]) => ({
        endpoint,
        requestsLastMinute: timestamps.length,
        isHighFrequency: timestamps.length > this.requestLimitPerMinute
      }))
      .sort((a, b) => b.requestsLastMinute - a.requestsLastMinute);

    return {
      ...this.stats,
      successRate: this.stats.totalRequests > 0 
        ? Math.round(((this.stats.totalRequests - this.stats.totalErrors) / this.stats.totalRequests) * 100)
        : 100,
      recentRequests: recentRequests.slice(0, 10), // Last 10 requests
      endpointStats,
      highFrequencyEndpoints: endpointStats.filter(stat => stat.isHighFrequency)
    };
  }

  /**
   * Show performance stats in console (dev only)
   */
  showStats() {
    if (!this.isDevelopment) return;

    const stats = this.getStats();
    
    console.group('📊 API Monitor Statistics');
    console.log('Total Requests:', stats.totalRequests);
    console.log('Success Rate:', `${stats.successRate}%`);
    console.log('Average Response Time:', `${stats.averageResponseTime}ms`);
    console.log('Slow Requests:', stats.slowRequests);
    console.log('Total Errors:', stats.totalErrors);
    
    if (stats.highFrequencyEndpoints.length > 0) {
      console.warn('High Frequency Endpoints:', stats.highFrequencyEndpoints);
    }
    
    if (stats.recentRequests.length > 0) {
      console.log('Recent Requests:', stats.recentRequests);
    }
    
    console.groupEnd();
  }

  /**
   * Reset all statistics
   */
  reset() {
    this.requests.clear();
    this.endpointFrequency?.clear();
    this.stats = {
      totalRequests: 0,
      totalErrors: 0,
      slowRequests: 0,
      averageResponseTime: 0
    };
  }
}

// Export singleton instance
const apiMonitor = new ApiMonitor();

// Auto-show stats every 5 minutes in development
if (apiMonitor.isDevelopment) {
  setInterval(() => {
    if (apiMonitor.stats.totalRequests > 0) {
      apiMonitor.showStats();
    }
  }, 300000); // 5 minutes
}

export default apiMonitor;
